<template>
    <BaseModal
        class="modal-manage-groups"
        modal-size="extra-large"
        :scroll-content="false"
        :show-footer="false"
        @close="$emit('close')">
        <template #header>
            <div>
                <h5 class="mb-1">
                    <i
                        aria-hidden="true"
                        class="fas fa-users text-primary mr-2" />Groups
                </h5>

                <p class="modal-manage-groups__subtitle mb-0">
                    Write a group's @name in a thread to tag everyone in it at once.
                </p>
            </div>
        </template>

        <template #body>
            <div class="modal-manage-groups__panes">
                <aside class="modal-manage-groups__list">
                    <div class="modal-manage-groups__toolbar">
                        <input
                            ref="search"
                            v-model.trim="search"
                            type="search"
                            class="form-control"
                            placeholder="Search groups"
                            aria-label="Search groups">

                        <button
                            ref="newGroupButton"
                            type="button"
                            class="btn btn-primary-transparent text-nowrap"
                            @click="startNewGroup">
                            <i
                                aria-hidden="true"
                                class="fas fa-plus mr-1" /> New Group
                        </button>
                    </div>

                    <p
                        v-if="isLoading"
                        class="modal-manage-groups__notice">
                        Loading groups…
                    </p>

                    <p
                        v-else-if="error"
                        class="modal-manage-groups__notice text-danger">
                        Groups could not be loaded.
                    </p>

                    <p
                        v-else-if="!matchingGroups.length"
                        class="modal-manage-groups__notice">
                        {{ nothingListed }}
                    </p>

                    <ul
                        v-else-if="isShowingArchived"
                        class="modal-manage-groups__groups">
                        <li
                            v-for="group in matchingGroups"
                            :key="group.uuid"
                            class="modal-manage-groups__group modal-manage-groups__group--archived">
                            <span
                                aria-hidden="true"
                                class="modal-manage-groups__group-icon">
                                <i class="fas fa-archive" />
                            </span>

                            <span class="modal-manage-groups__group-label">
                                <span class="modal-manage-groups__group-name">@{{ group.name }}</span>

                                <span class="modal-manage-groups__group-count">
                                    {{ memberCountOf(group) }} ·
                                    archived {{ fromNow(group.archived_at) }}
                                </span>
                            </span>

                            <button
                                type="button"
                                class="btn btn-sm btn-primary-transparent"
                                :disabled="restoringUuid === group.uuid"
                                @click="restoreGroup(group)">
                                Restore
                            </button>
                        </li>
                    </ul>

                    <ul
                        v-else
                        class="modal-manage-groups__groups">
                        <li
                            v-for="group in matchingGroups"
                            :key="group.uuid">
                            <button
                                type="button"
                                class="modal-manage-groups__group"
                                :class="{ 'modal-manage-groups__group--active': isEditing(group) }"
                                :aria-current="isEditing(group)"
                                @click="openGroup(group)">
                                <span
                                    aria-hidden="true"
                                    class="modal-manage-groups__group-icon">
                                    <i class="fas fa-users" />
                                </span>

                                <span class="modal-manage-groups__group-name">@{{ group.name }}</span>

                                <span class="modal-manage-groups__group-count">
                                    {{ memberCountOf(group) }}
                                </span>
                            </button>
                        </li>
                    </ul>

                    <button
                        type="button"
                        class="btn btn-link modal-manage-groups__archived-toggle"
                        @click="toggleArchived">
                        <i
                            aria-hidden="true"
                            :class="`fas ${isShowingArchived ? 'fa-arrow-left' : 'fa-archive'} mr-1`" />
                        {{ isShowingArchived ? 'Back to groups' : 'Archived groups' }}
                    </button>
                </aside>

                <section class="modal-manage-groups__editor">
                    <p
                        v-if="isOpeningGroup"
                        class="modal-manage-groups__placeholder">
                        Opening group…
                    </p>

                    <div
                        v-else-if="!editingGroup"
                        class="modal-manage-groups__placeholder">
                        <i
                            aria-hidden="true"
                            class="fas fa-user-friends modal-manage-groups__placeholder-icon" />

                        <p class="mb-0">
                            Pick a group to edit it, or start a new one.
                        </p>
                    </div>

                    <FormMentionGroup
                        v-else
                        :key="editingGroup.uuid || 'new-group'"
                        :mention-group="editingGroup"
                        @saved="editingGroup = $event"
                        @archived="closeEditor"
                        @cancel="closeEditor">
                    </FormMentionGroup>
                </section>
            </div>
        </template>
    </BaseModal>
</template>

<script>
import { escape } from 'lodash'
import { mapActions, mapState } from 'pinia'
import { BaseModal, refusalOf } from '@technologyadvice/relay-frontend'
import FormMentionGroup from '@components/mention-groups/FormMentionGroup.vue'
import { useMentionGroupsStore } from '@store/mentionGroups'
import { fromNow } from '@util/dates'
import { memberCountOf } from '@util/mentionGroups'

export default {
    name: 'ModalManageGroups',
    components: {
        BaseModal,
        FormMentionGroup,
    },
    data() {
        return {
            search: '',
            editingGroup: null,
            isOpeningGroup: false,
            isShowingArchived: false,
            restoringUuid: null,
        }
    },
    computed: {
        ...mapState(useMentionGroupsStore, [ 'mentionGroups', 'archivedGroups', 'isLoading', 'error' ]),
        matchingGroups() {
            const term = this.search.toLowerCase()
            const listed = this.isShowingArchived ? this.archivedGroups : this.mentionGroups

            return listed.filter(({ name }) => name.toLowerCase().includes(term))
        },
        nothingListed() {
            if (this.search) {
                return 'No group matches your search.'
            }

            return this.isShowingArchived ? 'No archived groups.' : 'No groups yet.'
        },
    },
    created() {
        this.load()
    },
    mounted() {
        this.$refs.search.focus()
    },
    methods: {
        ...mapActions(useMentionGroupsStore, [ 'load', 'fetchMentionGroup', 'loadArchived', 'restore' ]),
        startNewGroup() {
            this.editingGroup = { uuid: null, name: '', members: [] }
        },
        async openGroup({ uuid, name }) {
            this.isOpeningGroup = true

            try {
                this.editingGroup = await this.fetchMentionGroup(uuid)
            } catch {
                this.$_toasterDanger(`Could not open @${escape(name)}. Try again.`)
            } finally {
                this.isOpeningGroup = false
            }
        },
        async toggleArchived() {
            this.isShowingArchived = !this.isShowingArchived

            if (!this.isShowingArchived) {
                return
            }

            try {
                await this.loadArchived()
            } catch {
                this.$_toasterDanger('Could not load the archived groups. Try again.')
            }
        },
        async restoreGroup({ uuid, name }) {
            this.restoringUuid = uuid

            try {
                await this.restore(uuid)
                this.$_toasterSuccess(`Restored @${escape(name)}`)
            } catch (error) {
                this.$_toasterDanger(escape(refusalOf(error, `Could not restore @${name}. Try again.`)))
            } finally {
                this.restoringUuid = null
            }
        },
        closeEditor() {
            this.editingGroup = null
            this.$refs.newGroupButton.focus()
        },
        isEditing({ uuid }) {
            return this.editingGroup?.uuid === uuid
        },
        memberCountOf,
        fromNow,
    },
}
</script>
